/**
 * ConfirmModal: renders a modal to prompt the user for confirmation
 * props:
 *            isOpen (bool): whether the modal is open
 *         headerText (str): text to be rendered in the header of the modal
 *   confirmationText (str): text to be rendered inside the modal
 *      onCancel (function): callback function when the modal is canceled/closed
 *     onConfirm (function): callback function when the modal is confirmed
 */

import React from 'react';
import PropTypes from 'prop-types';
import I18n from 'i18n-js';
import Modal from './Modal';
import styles from './Modal.module.scss';
import Spinner from './Spinner';
import { Footer } from './index';

interface ConfirmModalProps {
  confirmButtonText?: string;
  confirmationText: string;
  headerText: string;
  isLoading: boolean;
  isOpen: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

const ConfirmModal = ({
  confirmationText, headerText, isLoading, isOpen, onCancel, onConfirm, confirmButtonText,
}: ConfirmModalProps) => {
  const footer = () => (
    <Footer
      primaryButtonCallback={onConfirm}
      primaryButtonClassName="btn btn--purple"
      primaryButtonSubmit={false}
      primaryButtonText={confirmButtonText}
      primaryButtonDisabled={isLoading}
      secondaryButtonCallback={onCancel}
      wrapperClassName="tw-pr-4 tw-pb-4"
    />
  );

  return (
    <Modal
      className={styles.confirm}
      isOpen={isOpen}
      closeModal={onCancel}
      headerText={headerText}
      footer={footer()}
    >
      {isLoading ? <Spinner isLoading={isLoading} /> : confirmationText}
    </Modal>
  );
};

ConfirmModal.propTypes = {
  confirmationText: PropTypes.string.isRequired,
  confirmButtonText: PropTypes.string,
  headerText: PropTypes.string.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

ConfirmModal.defaultProps = {
  confirmButtonText: `${I18n.t('yes')}, ${I18n.t('delete')}`,
};

export default ConfirmModal;
